import React, { FC, PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';

import { BaseStyle, BaseStyleEnums, CoreUtils } from '@nhl/nhl-breakaway-ui';
import { useScroll } from '@nhl/react-hooks/lib/useScroll';

import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { isPageZoomed } from '~/core/utils';
import { CommonGCGame, GameCenterTeam } from '~/views/pages/gameCenter/types';
import { PlayoffSeriesGame, PlayoffSeriesTeam } from '~/views/pages/playoffSeries/types';

import ScoreboardDecorator from './components/scoreboardDecorator/ScoreboardDecorator';
import StylizedTeam from './components/stylizedTeam/StylizedTeam';
import { GameCenterOrPlayoffSeriesTeam } from './types';
import { getScoreboardDescriptionFromGame } from './utils';

import { HiddenDiv, ScoreboardContainer } from './styles';

type GameOrSeriesScoreboardProps = {
    game?: CommonGCGame | PlayoffSeriesGame;
    leftTeam: GameCenterTeam | PlayoffSeriesTeam;
    rightTeam: GameCenterTeam | PlayoffSeriesTeam;
    isSeries?: boolean;
    hideRecord?: boolean;
};

const GameOrSeriesScoreboard: FC<PropsWithChildren<GameOrSeriesScoreboardProps>> = ({
    children,
    game = null,
    isSeries,
    leftTeam,
    rightTeam,
    hideRecord = false,
}) => {
    const isWebview = useIsWebview();
    const screenSize = useSizeWithThrottle();
    const isMediumScreenOrSmaller = screenSize.width < BaseStyleEnums.ScreenWidthBreakpoints.medium;

    // As the user scrolls on the page we get the position of the scoreboard. Once it reaches the top we expand it
    const scoreboardRef = useRef<HTMLDivElement>(null);
    const [shouldExpandScoreboardContainer, setShouldExpandScoreboardContainer] = useState<boolean>(false);

    const onScrollChange = useCallback(() => {
        const headerHeight = isWebview ? 0 : BaseStyle.LEAGUE_HEADER_HEIGHT;
        const top = scoreboardRef.current?.getBoundingClientRect()?.top ?? Infinity;
        setShouldExpandScoreboardContainer(headerHeight >= top);
    }, [isWebview]);

    useScroll({
        onScrollChanged: onScrollChange,
    });

    const preGame = (game && CoreUtils.isPregame(game?.gameState)) || false;

    const getScoreboardDescriptionString = useMemo(() => {
        if (isSeries || !game) {
            return '';
        }
        return getScoreboardDescriptionFromGame(game as CommonGCGame, preGame);
    }, [game, isSeries, preGame]);

    const hideTeamRecordAndTricode = isSeries && isMediumScreenOrSmaller;

    const isZoomed = isPageZoomed();

    return (
        <>
            {/*we hide the ScoreboardContainer from screen readers because its contents aren't very coherent*/}
            {/*and instead put a more descriptive string in HiddenDiv*/}
            <ScoreboardContainer
                aria-hidden
                ref={scoreboardRef}
                role="heading"
                $isExpanded={shouldExpandScoreboardContainer}
                $isSeries={isSeries}
                $isWebview={isWebview}
                $isSticky={!isZoomed}
            >
                <ScoreboardDecorator
                    isHome={false}
                    team={(isSeries ? leftTeam : game?.awayTeam) as GameCenterOrPlayoffSeriesTeam}
                    isExpanded={shouldExpandScoreboardContainer}
                />
                <StylizedTeam
                    hideRecord={hideRecord || hideTeamRecordAndTricode}
                    hideTricode={hideTeamRecordAndTricode}
                    isHome={false}
                    isSeries={isSeries}
                    team={(isSeries ? leftTeam : game?.awayTeam) as GameCenterOrPlayoffSeriesTeam}
                    isPreGame={preGame}
                    isExpanded={shouldExpandScoreboardContainer}
                    situation={isSeries ? undefined : (game as CommonGCGame)?.situation}
                />
                {children}
                <StylizedTeam
                    hideRecord={hideRecord || hideTeamRecordAndTricode}
                    hideTricode={hideTeamRecordAndTricode}
                    isHome
                    isSeries={isSeries}
                    team={(isSeries ? rightTeam : game?.homeTeam) as GameCenterOrPlayoffSeriesTeam}
                    isPreGame={preGame}
                    isExpanded={shouldExpandScoreboardContainer}
                    situation={isSeries ? undefined : (game as CommonGCGame)?.situation}
                />
                <ScoreboardDecorator
                    isHome
                    team={(isSeries ? rightTeam : game?.homeTeam) as GameCenterOrPlayoffSeriesTeam}
                    isExpanded={shouldExpandScoreboardContainer}
                />
            </ScoreboardContainer>
            <HiddenDiv>{getScoreboardDescriptionString}</HiddenDiv>
        </>
    );
};

export default GameOrSeriesScoreboard;
